import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '../../services/auth/tokenStorage';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Share,
  Alert,
  Platform,
} from 'react-native';
import { ms, s, vs } from '../../utils/responsive';
import { NavigationProp } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import Clipboard from '@react-native-clipboard/clipboard';
import { API_URL } from '../../config';

// Import icons
import backIcon from '../../assets/icons/back.png';
import copyIcon from '../../assets/icons/copy.png';

import { createLogger } from '../../utils/logger';
const logger = createLogger('ReferAFriend');

interface ReferAFriendScreenProps {
  navigation: NavigationProp<any>;
}

interface ReferralInfo {
  code: string;
  link: string;
  totalReferrals: number;
  totalRewardDays: number;
  pendingReferrals: number;
  activeReferrals: number;
}

const ReferAFriendScreen: React.FC<ReferAFriendScreenProps> = ({ navigation }) => {
  const [info, setInfo] = useState<ReferralInfo | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchReferralInfo = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const token = await getAuthToken();
      if (!token) {
        setError('You are not signed in. Please sign in and try again.');
        return;
      }

      const response = await fetch(`${API_URL}/user/referral/info`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data: ReferralInfo = await response.json();
        setInfo(data);
      } else {
        logger.error('Failed to fetch referral info. Status:', response.status);
        setError('Unable to load your referral info. Please try again.');
      }
    } catch (err) {
      logger.error('Error fetching referral info:', err);
      setError('Something went wrong. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchReferralInfo();
  }, [fetchReferralInfo]);

  const handleCopyCode = () => {
    if (!info?.code) return;
    try {
      Clipboard.setString(info.code);
      Alert.alert('Copied!', 'Your referral code is copied to clipboard');
    } catch (err) {
      logger.error('Failed to copy code:', err);
      Alert.alert('Your Referral Code', info.code);
    }
  };

  const handleCopyLink = () => {
    if (!info?.link) return;
    try {
      Clipboard.setString(info.link);
      Alert.alert('Copied!', 'Your referral link is copied to clipboard');
    } catch (err) {
      logger.error('Failed to copy link:', err);
      Alert.alert('Your Referral Link', info.link);
    }
  };

  const handleShare = async () => {
    if (!info?.link) return;
    try {
      await Share.share({
        message: `Check out MyFinalKey - a secure way to organize your final wishes for your loved ones. Use my link to get 1 month free: ${info.link}`,
      });
    } catch (err) {
      logger.error('Failed to open share sheet:', err);
    }
  };

  const renderHeader = () => (
    <View style={styles.header}>
      <TouchableOpacity onPress={() => navigation.goBack()}>
        <Image source={backIcon} style={styles.headerIcon} />
      </TouchableOpacity>
      <Text style={styles.headerTitle}>Refer a Friend</Text>
      <View style={styles.placeholder} />
    </View>
  );

  if (isLoading) {
    return (
      <SafeAreaView style={styles.container}>
        {renderHeader()}
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color="#29BFC0" />
        </View>
      </SafeAreaView>
    );
  }

  if (error || !info) {
    return (
      <SafeAreaView style={styles.container}>
        {renderHeader()}
        <View style={styles.errorContainer}>
          <Text style={styles.errorTitle}>Oops!</Text>
          <Text style={styles.errorText}>
            {error || 'Unable to load your referral info.'}
          </Text>
          <TouchableOpacity
            style={styles.retryButton}
            onPress={fetchReferralInfo}
          >
            <Text style={styles.retryButtonText}>Try Again</Text>
          </TouchableOpacity>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      {renderHeader()}

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Hero Section */}
        <View style={styles.heroCard}>
          <Text style={styles.heroTitle}>Invite friends to MyFinalKey</Text>
          <Text style={styles.heroSubtitle}>
            You get 2 months free for each friend who subscribes. They get 1 month
            free added to their annual plan.
          </Text>
        </View>

        {/* Stats Cards */}
        <View style={styles.statsGrid}>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{info.totalReferrals}</Text>
            <Text style={styles.statLabel}>Friends Referred</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{info.totalRewardDays}</Text>
            <Text style={styles.statLabel}>Reward Days Earned</Text>
          </View>
          <View style={styles.statCard}>
            <Text style={styles.statValue}>{info.activeReferrals}</Text>
            <Text style={styles.statLabel}>Active Referrals</Text>
          </View>
        </View>

        {/* Referral Code */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Your Referral Code</Text>
          <View style={styles.codeBox}>
            <Text style={styles.codeText}>{info.code}</Text>
          </View>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleCopyCode}
          >
            <Image source={copyIcon} style={styles.buttonIcon} />
            <Text style={styles.primaryButtonText}>Copy Code</Text>
          </TouchableOpacity>
        </View>

        {/* Referral Link */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>Your Referral Link</Text>
          <View style={styles.linkBox}>
            <Text style={styles.linkText} numberOfLines={2} ellipsizeMode="middle">
              {info.link}
            </Text>
          </View>
          <TouchableOpacity
            style={styles.secondaryButton}
            onPress={handleCopyLink}
          >
            <Image source={copyIcon} style={[styles.buttonIcon, styles.buttonIconTeal]} />
            <Text style={styles.secondaryButtonText}>Copy Link</Text>
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.primaryButton}
            onPress={handleShare}
          >
            <Text style={styles.primaryButtonText}>Share</Text>
          </TouchableOpacity>
        </View>

        {/* How it works */}
        <View style={styles.sectionCard}>
          <Text style={styles.sectionTitle}>How it works</Text>

          <View style={styles.step}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>1</Text>
            </View>
            <Text style={styles.stepText}>
              Share your code or link with a friend
            </Text>
          </View>

          <View style={styles.step}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>2</Text>
            </View>
            <Text style={styles.stepText}>They sign up and subscribe</Text>
          </View>

          <View style={styles.step}>
            <View style={styles.stepBadge}>
              <Text style={styles.stepBadgeText}>3</Text>
            </View>
            <Text style={styles.stepText}>
              You both get free time added to your subscription!
            </Text>
          </View>
        </View>

        <View style={styles.bottomPadding} />
      </ScrollView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F3F6F8',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: ms(20),
    paddingVertical: ms(15),
    marginTop: ms(10),
    backgroundColor: '#F3F6F8',
  },
  headerTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
  },
  headerIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#1C2C4F',
  },
  placeholder: {
    width: s(24),
  },
  content: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    paddingHorizontal: ms(30),
  },
  errorTitle: {
    fontSize: ms(22, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  errorText: {
    fontSize: ms(15, 0.3),
    color: '#666',
    textAlign: 'center',
    marginBottom: ms(20),
    lineHeight: ms(22, 0.3),
  },
  retryButton: {
    backgroundColor: '#29BFC0',
    paddingHorizontal: ms(30),
    paddingVertical: ms(12),
    borderRadius: ms(50),
  },
  retryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  heroCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  heroTitle: {
    fontSize: ms(20, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(10),
  },
  heroSubtitle: {
    fontSize: ms(15, 0.3),
    color: '#666',
    lineHeight: ms(22, 0.3),
  },
  statsGrid: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: ms(20),
  },
  statCard: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(12),
    marginHorizontal: ms(4),
    alignItems: 'center',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  statValue: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
    marginBottom: ms(4),
  },
  statLabel: {
    fontSize: ms(12, 0.3),
    color: '#666',
    textAlign: 'center',
  },
  sectionCard: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  sectionTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: 'bold',
    color: '#1C2C4F',
    marginBottom: ms(15),
  },
  codeBox: {
    backgroundColor: '#F3F6F8',
    borderRadius: ms(12),
    paddingVertical: ms(20),
    paddingHorizontal: ms(15),
    alignItems: 'center',
    marginBottom: ms(15),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  codeText: {
    fontSize: ms(28, 0.3),
    fontWeight: 'bold',
    color: '#29BFC0',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    letterSpacing: ms(2),
  },
  linkBox: {
    backgroundColor: '#F3F6F8',
    borderRadius: ms(12),
    padding: ms(15),
    marginBottom: ms(15),
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  linkText: {
    fontSize: ms(14, 0.3),
    color: '#1C2C4F',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  primaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#29BFC0',
    borderRadius: ms(50),
    paddingVertical: ms(14),
    paddingHorizontal: ms(20),
    marginTop: ms(10),
  },
  primaryButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  secondaryButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: ms(50),
    paddingVertical: ms(14),
    paddingHorizontal: ms(20),
    borderWidth: ms(1.5),
    borderColor: '#29BFC0',
  },
  secondaryButtonText: {
    color: '#29BFC0',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  buttonIcon: {
    width: s(18),
    height: s(18),
    tintColor: '#FFFFFF',
    marginRight: ms(8),
  },
  buttonIconTeal: {
    tintColor: '#29BFC0',
  },
  step: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: ms(15),
  },
  stepBadge: {
    width: s(32),
    height: s(32),
    borderRadius: ms(16),
    backgroundColor: '#29BFC0',
    alignItems: 'center',
    justifyContent: 'center',
    marginRight: ms(15),
  },
  stepBadgeText: {
    color: '#FFFFFF',
    fontSize: ms(15, 0.3),
    fontWeight: 'bold',
  },
  stepText: {
    flex: 1,
    fontSize: ms(15, 0.3),
    color: '#1C2C4F',
    lineHeight: ms(22, 0.3),
  },
  bottomPadding: {
    height: vs(30),
  },
});

export default ReferAFriendScreen;
